// API Client - Axios instance with interceptors
import axios, { AxiosError, AxiosInstance } from 'axios';
import { API_BASE_URL } from '@/constants/config';
import type { ApiResponse } from '@/types/api/response.types';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('carleads_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<ApiResponse>) => {
    // Handle common errors
    if (error.response) {
      const { status, data } = error.response;

      // Unauthorized - clear token and redirect
      if (status === 401) {
        localStorage.removeItem('carleads_auth_token');
        window.location.href = '/auth/login';
      }

      // Return structured error
      return Promise.reject({
        message: data?.error || 'An error occurred',
        code: data?.code,
        errors: data?.errors,
        status,
      });
    }

    // Network error
    if (error.request) {
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      });
    }

    // Other errors
    return Promise.reject({
      message: error.message || 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
    });
  }
);

export default apiClient;
